import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom";

/**
 * `next/navigation`'s call-site shape over React Router.
 *
 * Used by 22 files in the delivered console. Preserving the shape keeps the SPA
 * port mechanical; call sites can migrate to React Router's own hooks
 * incrementally, since both resolve to the same underlying router.
 */

/** `next/navigation`'s NavigateOptions. `scroll: false` is React Router's `preventScrollReset`. */
export interface NavigateOptions {
  scroll?: boolean;
}

export interface AppRouter {
  push(href: string, options?: NavigateOptions): void;
  replace(href: string, options?: NavigateOptions): void;
  back(): void;
  forward(): void;
  /** Next re-fetched server components here. A client-only SPA has nothing to refresh. */
  refresh(): void;
  prefetch(href: string): void;
}

export function useRouter(): AppRouter {
  const navigate = useNavigate();
  return useMemo<AppRouter>(
    () => ({
      push: (href, options) =>
        navigate(href, { preventScrollReset: options?.scroll === false }),
      replace: (href, options) =>
        navigate(href, { replace: true, preventScrollReset: options?.scroll === false }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => {},
      prefetch: () => {},
    }),
    [navigate],
  );
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return useRouterParams() as T;
}

/**
 * Returns the read-only params alone, as `next/navigation` does — React Router
 * returns a `[params, setParams]` tuple, and every call site here reads only.
 */
export function useSearchParams(): URLSearchParams {
  const [searchParams] = useRouterSearchParams();
  return searchParams;
}
